import { z } from 'zod'

import type { ActivityFormValues } from './values'

const MAX_DURATION_MS = 24 * 60 * 60 * 1000

function addDateIssue(
  ctx: z.RefinementCtx,
  path: 'startAt' | 'endAt',
  message: string,
): void {
  ctx.addIssue({ code: 'custom', path: [path], message })
}

function localDate(value: string): number {
  return value ? new Date(value).getTime() : Number.NaN
}

/**
 * Shared create/edit schema. Messages are catalog keys, never user-facing copy,
 * so Italian and English share one validation contract.
 *
 * Date fields use the browser's local `datetime-local` format; conversion to
 * UTC happens only in the request payload. City/place selection is validated by
 * the location hook because editing an unchanged location is explicitly
 * allowed without re-picking a suggestion (`f472f9a`).
 */
export const activityFormSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1, 'activityForm.validation.titleRequired')
      .max(120, 'activityForm.validation.titleTooLong'),
    description: z
      .string()
      .max(3000, 'activityForm.validation.descriptionTooLong'),
    categoryId: z
      .string()
      .min(1, 'activityForm.validation.categoryRequired'),
    startAt: z.string().min(1, 'activityForm.validation.startRequired'),
    endAt: z.string().min(1, 'activityForm.validation.endRequired'),
    city: z.string().max(120, 'activityForm.validation.cityTooLong'),
    locationName: z
      .string()
      .max(160, 'activityForm.validation.placeTooLong'),
    meetingPoint: z
      .string()
      .max(300, 'activityForm.validation.meetingPointTooLong'),
    maxParticipants: z
      .string()
      .min(1, 'activityForm.validation.maxParticipantsRequired')
      .refine(
        (value) => Number.isInteger(Number(value)) && Number(value) >= 1,
        'activityForm.validation.maxParticipants',
      ),
    participationMode: z.enum(['open', 'approval', 'invite']),
    visibility: z.enum(['public', 'friends', 'private']),
    repeatWeekly: z.boolean(),
    hasExpenses: z.boolean(),
    estimatedPrice: z.string(),
    priceDescription: z
      .string()
      .max(500, 'activityForm.validation.priceDescriptionTooLong'),
  })
  .superRefine((values, ctx) => {
    const start = localDate(values.startAt)
    const end = localDate(values.endAt)

    if (Number.isNaN(start)) {
      addDateIssue(ctx, 'startAt', 'activityForm.validation.startRequired')
    } else if (start <= Date.now()) {
      addDateIssue(ctx, 'startAt', 'activityForm.validation.startFuture')
    }

    if (Number.isNaN(end)) {
      addDateIssue(ctx, 'endAt', 'activityForm.validation.endRequired')
    } else if (!Number.isNaN(start) && end <= start) {
      addDateIssue(ctx, 'endAt', 'activityForm.validation.endAfterStart')
    } else if (!Number.isNaN(start) && end - start > MAX_DURATION_MS) {
      addDateIssue(ctx, 'endAt', 'activityForm.validation.duration')
    }

    if (values.hasExpenses) {
      const normalized = values.estimatedPrice.trim().replace(',', '.')
      const price = Number(normalized)
      if (!normalized || !Number.isFinite(price) || price < 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['estimatedPrice'],
          message: 'activityForm.validation.priceRequired',
        })
      }
    }
  })

export type ActivityFormSchemaValues = z.infer<typeof activityFormSchema>

export function normalizePrice(value: string): number {
  return Number(value.trim().replace(',', '.'))
}

export function isActivityFormValues(
  value: unknown,
): value is ActivityFormValues {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return (
    typeof record.title === 'string' &&
    typeof record.description === 'string' &&
    typeof record.categoryId === 'string' &&
    typeof record.startAt === 'string' &&
    typeof record.endAt === 'string' &&
    typeof record.city === 'string' &&
    typeof record.locationName === 'string' &&
    typeof record.meetingPoint === 'string' &&
    typeof record.maxParticipants === 'string' &&
    ['open', 'approval', 'invite'].includes(
      typeof record.participationMode === 'string'
        ? record.participationMode
        : '',
    ) &&
    ['public', 'friends', 'private'].includes(
      typeof record.visibility === 'string' ? record.visibility : '',
    ) &&
    typeof record.repeatWeekly === 'boolean' &&
    typeof record.hasExpenses === 'boolean' &&
    typeof record.estimatedPrice === 'string' &&
    typeof record.priceDescription === 'string'
  )
}
